import { useEffect, useState } from 'react'
import { FileUpload } from '../components/FileUpload'
import { FileList } from '../components/FileList'
import { ShareModal } from '../components/ShareModal'
import { useFiles } from '../hooks/useFiles'

export function Dashboard() {
  const {
    files,
    isLoading,
    error,
    uploadProgress,
    refreshFiles,
    upload,
    download,
    remove,
    share,
  } = useFiles()

  const [shareFile, setShareFile] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  useEffect(() => {
    refreshFiles()
  }, [refreshFiles])

  const handleDelete = async (key: string) => {
    setDeleteConfirm(key)
  }

  const confirmDelete = async () => {
    if (deleteConfirm) {
      try {
        await remove(deleteConfirm)
      } catch {
        // Error is handled by the hook
      }
      setDeleteConfirm(null)
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">My Files</h1>
        <p className="mt-1 text-sm text-gray-600">
          Upload, download, and share your files securely
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <FileUpload
        onUpload={upload}
        progress={uploadProgress}
        disabled={isLoading}
      />

      <FileList
        files={files}
        isLoading={isLoading}
        onDownload={download}
        onDelete={handleDelete}
        onShare={(key) => setShareFile(key)}
      />

      {shareFile && (
        <ShareModal
          filename={shareFile}
          isOpen={true}
          onClose={() => setShareFile(null)}
          onShare={(expiresIn, password) => share(shareFile, expiresIn, password)}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75"
              onClick={() => setDeleteConfirm(null)}
            ></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Delete File
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete{' '}
                <span className="font-medium">{deleteConfirm}</span>? This action
                cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
